import { useCallback, useMemo } from 'react';

import { isGroupHeader } from '../helpers';
import {
  areGroupedItems,
  flattenGroupItemsToDownshift,
  flattenGroupItemsToNonDownshift,
  getStartingIndices,
} from '../helpers/groupedItems';
import isHiddenMenuItem from '../helpers/isHiddenMenuItem';
import { FlatGroupedItem, GenericItem, GroupHeader, ItemsProp } from '../types';

export default <TItem extends GenericItem>({
  maybeGroupedItems,
  allowCollapsibleGroups,
  expandedGroupKey,
  selectedGroupKey,
}: {
  maybeGroupedItems: ItemsProp<TItem>;
  allowCollapsibleGroups?: boolean;
  expandedGroupKey?: string;
  selectedGroupKey?: string;
}) => {
  const isGrouped = useMemo(
    () => areGroupedItems(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const flatItems = useMemo(
    () => flattenGroupItemsToDownshift(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const visibleFlatItems = useMemo(
    () => flatItems.filter((item) => !isHiddenMenuItem(item)),
    [flatItems],
  );

  const { flatItemsWithGroupHeaders, mapDownshiftItemToItemWithGroupInfo } =
    useMemo(() => {
      if (!areGroupedItems(maybeGroupedItems)) {
        return {
          startingIndices: new Map<number, number>(),
          indexGetter: undefined,
        };
      }

      const startingIndices = getStartingIndices(maybeGroupedItems);
      const flatItemsWithGroupHeaders = flattenGroupItemsToNonDownshift(
        maybeGroupedItems,
        startingIndices,
      );
      const flatItemsWithGroupInfo = flatItemsWithGroupHeaders.filter(
        (item) => !isGroupHeader(item),
      ) as FlatGroupedItem<TItem>[]; // The filter removes the GroupHeaders, but they're still included in the type

      const mapDownshiftItemToItemWithGroupInfo = flatItems.reduce(
        (map, curr, i) => {
          map.set(curr, flatItemsWithGroupInfo[i]);

          return map;
        },
        new Map<TItem, FlatGroupedItem<TItem>>(),
      );

      return {
        flatItemsWithGroupHeaders,
        mapDownshiftItemToItemWithGroupInfo,
      };
    }, [flatItems, maybeGroupedItems]);

  /**
   * Gets the index for downshift
   * We need to determine this manually, so we skip over group headers
   */
  const indexGetter = useMemo(
    () =>
      isGrouped
        ? (item: TItem): number | undefined =>
            mapDownshiftItemToItemWithGroupInfo?.get(item)?.index
        : null,
    [isGrouped, mapDownshiftItemToItemWithGroupInfo],
  );

  /** Adds group headers for provided items
   * @param itemsToShow The (potentially) filtered list of items to show
   */
  const getMenuItems = useCallback(
    (itemsToShow: TItem[], countItems = true) => {
      if (!isGrouped) {
        return itemsToShow;
      }

      const filteredItemsToShow = itemsToShow.filter(
        (item) => !isHiddenMenuItem(item),
      );

      if (filteredItemsToShow.length <= 0) {
        return filteredItemsToShow;
      }

      const filteredGroupedMenuItems: (GroupHeader | TItem)[] = [];

      const groupHeadersMap = new Map();
      const groupHeadersArray: (GroupHeader | FlatGroupedItem<TItem>)[] = [];
      flatItemsWithGroupHeaders?.forEach((item) => {
        if (item.isGroupHeader) {
          groupHeadersMap.set(item.groupKey, item);
          if ((item as GroupHeader).itemCount > 0) {
            groupHeadersArray.push(item);
          }
        }
      });

      const filteredGroupedMenuItemsSet = new Set();
      let groupIndex = 0;

      filteredItemsToShow.forEach((filteredItem, i) => {
        const filteredGroupedItem =
          mapDownshiftItemToItemWithGroupInfo?.get(filteredItem);
        if (!filteredGroupedItem) {
          return;
        }

        const groupHeader = groupHeadersMap.get(filteredGroupedItem.groupKey);

        if (groupHeader && !filteredGroupedMenuItemsSet.has(groupHeader)) {
          const groupHeaderIndex = groupHeadersArray.indexOf(groupHeader);

          // if the group header is before the current group index, add all group headers
          // with zero filtered item counts
          if (groupHeaderIndex >= groupIndex) {
            for (let i = groupIndex; i <= groupHeaderIndex; i++) {
              if (!filteredGroupedMenuItemsSet.has(groupHeader)) {
                filteredGroupedMenuItemsSet.add(groupHeadersArray[i]);
                filteredGroupedMenuItems.push(groupHeadersArray[i]);
                if (countItems) {
                  groupHeadersArray[i].filteredItemCount = 0;
                }
              }
            }

            groupIndex = groupHeaderIndex + 1;
          }
        }

        filteredGroupedItem.index = i;

        // if collapsible groups enabled then only include items in the dropdown
        // if the group is expanded
        if (
          !allowCollapsibleGroups ||
          groupHeader.groupKey === expandedGroupKey ||
          groupHeader.groupKey === selectedGroupKey
        ) {
          filteredGroupedMenuItems.push(filteredItem);
        }

        // always count items in the group regardless if group is collapsed or not
        if (countItems) {
          groupHeader.filteredItemCount =
            (groupHeader.filteredItemCount ?? 0) + 1;
        }
      });

      // if there are empty group headers that were not added to the list, add them now
      if (groupIndex < groupHeadersArray.length) {
        for (let i = groupIndex; i < groupHeadersArray.length; i++) {
          filteredGroupedMenuItems.push(groupHeadersArray[i]);
          if (countItems) {
            groupHeadersArray[i].filteredItemCount = 0;
          }
        }
      }

      return filteredGroupedMenuItems;
    },
    [
      allowCollapsibleGroups,
      expandedGroupKey,
      flatItemsWithGroupHeaders,
      isGrouped,
      mapDownshiftItemToItemWithGroupInfo,
      selectedGroupKey,
    ],
  );

  return {
    flatItems: visibleFlatItems,
    indexGetter,
    getMenuItems,
  };
};
